import { FC, Fragment, useEffect, useRef, useState } from 'react'
import { Row, Col, Text, CardLink, Content, Image } from '@/client/components'
import { IconName } from '@/client/components/Icon/types'
import { useWindowSize } from '@/client/hooks'
import { SliderArrow } from '@/client/submodules/Slider/SliderArrow'
import * as S from './styles'
import { ProductInfosProps } from '../types'

export const ProductInfos: FC<ProductInfosProps> = ({
  product_information,
  tools_services,
  product_information_redesign
}) => {
  const defaultLocale = process.env.NEXT_PUBLIC_DEFAULT_LOCALE
  const { breakpoint, width, height } = useWindowSize()
  const [hasCurrentSlide, setCurrentSlide] = useState(1)

  const settings = {
    className: 'center',
    centerMode: true,
    infinite: true,
    centerPadding: '1px',
    slidesToShow: 3,
    speed: 1,
    swipe: false,
    prevArrow: <SliderArrow position='left' slidesToShow={3} shape='chevron' isInfinite />,
    nextArrow: <SliderArrow position='right' slidesToShow={3} shape='chevron' isInfinite />,
    afterChange: (currentSlide: number) => {
      setCurrentSlide(currentSlide + 1)
    }
  }

  const contentRefs = useRef<Array<HTMLDivElement | null>>([])
  const [contentHeights, setContentHeights] = useState<number[]>([])

  useEffect(() => {
    const heights = contentRefs.current.map((ref) => (ref ? ref.offsetHeight : 0))
    setContentHeights(heights)
  }, [breakpoint, width, height])

  return (
    <S.ProductInfos>
      <Row>
        <>
          {!product_information_redesign && (
            <Col xs={12} lg={12} xl={8} hd={9}>
              {product_information && <Content content={product_information} defaultLocale={defaultLocale} />}
            </Col>
          )}
        </>

        <>
          {product_information_redesign && (
            <Col xs={12} lg={12} xl={8} hd={9}>
              {product_information_redesign.map(
                (
                  {
                    key,
                    heading,
                    description,
                    background_color,
                    left_side_image,
                    image_array,
                    image_carousel,
                    right_side_image
                  },
                  index
                ) => (
                  <Fragment key={key}>
                    <S.Contents
                      ref={(el) => {
                        contentRefs.current[index] = el
                      }}
                      $defaultLocale={defaultLocale}
                    >
                      <S.ProductDetails>
                        {heading && (
                          <Text size='xlg' weight='bold'>
                            <span dangerouslySetInnerHTML={{ __html: heading }} />
                          </Text>
                        )}
                        {/* If there is an array of images on the top */}
                        {image_array && (
                          <>
                            <S.ImageArrayContainer>
                              {image_array.map(({ image, text, key }) => (
                                <Fragment key={key}>
                                  <div>
                                    <Image src={image.url} height='150' width='150' />
                                    <Text weight='bold' margin='1rem' align='center'>
                                      {text}
                                    </Text>
                                  </div>
                                </Fragment>
                              ))}
                            </S.ImageArrayContainer>
                          </>
                        )}

                        {/* If there is a left-side or a right-side image */}
                        <>
                          {left_side_image || right_side_image ? (
                            <S.SideImageAndTextAligner>
                              {left_side_image && (
                                <>
                                  <Image
                                    src={left_side_image.url}
                                    height={'270'}
                                    width={'270'}
                                    loading='lazy'
                                    className='left-side-image'
                                  />
                                  <Content content={description} className='content' />
                                </>
                              )}
                              {right_side_image && (
                                <>
                                  <Content content={description} className='content' />
                                  <Image
                                    src={right_side_image.url}
                                    height={'250'}
                                    width={'250'}
                                    loading='lazy'
                                    className='right-side-image'
                                  />
                                </>
                              )}
                            </S.SideImageAndTextAligner>
                          ) : (
                            <>
                              <Content content={description} />
                            </>
                          )}
                        </>

                        {/* If there is a carousel */}
                        {image_carousel && (
                          <S.Slide {...settings}>
                            {image_carousel.map(({ image, heading, key, description }) => (
                              <Fragment key={key}>
                                <S.ImageCarouselContainer>
                                  <Image
                                    src={image.url}
                                    height={+key === hasCurrentSlide ? '300' : '270'}
                                    width='450'
                                    className={+key === hasCurrentSlide ? 'activeImage' : 'notActiveImage'}
                                  />
                                  {+key === hasCurrentSlide && (
                                    <S.TextAligner>
                                      <Text weight='bold'>{heading}</Text>
                                      <Text size='xs' className='text-description'>
                                        {description}
                                      </Text>
                                    </S.TextAligner>
                                  )}
                                </S.ImageCarouselContainer>
                              </Fragment>
                            ))}
                          </S.Slide>
                        )}
                      </S.ProductDetails>
                      <S.Background height={contentHeights[index]} $background_color={background_color}></S.Background>
                    </S.Contents>
                  </Fragment>
                )
              )}
            </Col>
          )}
        </>

        {tools_services && (
          <Col xs={12} lg={12} xl={4} hd={3}>
            <S.Services>
              {tools_services?.title && (
                <Text as='h3' size='xlg' family='heading' weight='bold' margin='0 0 1.5rem'>
                  {tools_services.title}
                </Text>
              )}

              {tools_services?.infos &&
                tools_services?.infos.length > 0 &&
                tools_services?.infos.map(({ link, icon, title, subtitle }) => (
                  <CardLink key={title} link={link} icon={icon as IconName} title={title} subtitle={subtitle} />
                ))}
            </S.Services>
          </Col>
        )}
      </Row>
    </S.ProductInfos>
  )
}
